import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { FetchAllModules, FetchAllModulesSuccess } from 'app/library/all-modules/actions';
import { AllModuleDataSource } from 'app/library/all-modules/components/list/datasource';
import { IAllModuleDialogListInterface } from 'app/library/all-modules/interfaces/all-modules-dialog-list.interface';
import { AllModulesState } from 'app/library/all-modules/models/all-modules.state';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { IChoosenModulesInterface } from 'app/library/all-users/interfaces/choosen-modules.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UNASSIGNED_MODULE_FILTER_ID, UNASSIGNED_MODULE_FILTER_TYPE } from 'app/library/module/constants/unassigned-module-filter.constant';
import { Module } from 'app/library/module/models/module.model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TagService } from 'app/library/tag/services/tag.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { Observable, combineLatest } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-filter-by-module',
    templateUrl: './filter-by-module-dialog.component.html',
    styleUrls: ['./filter-by-module-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class FilterByModuleComponent extends UnsubscribeOnDestroy implements OnInit {
    private static readonly BOOKING_CONTEXT = 'booking';
    @Input()
    preSelectUnassigned: boolean;

    @Output()
    choosenModules = new EventEmitter<IChoosenModulesInterface>();

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    @Select(AllModulesState.getFilteredEntities)
    entitie$: Observable<Module[]>;

    @Select(AllModulesState.getTotalCount)
    totalCount$: Observable<number>;

    searchInput = new FormControl();
    isTableActive = true;
    isLoading = true;
    isUnassignedSelected: boolean;
    list: IChoosenFilterInterface[] = [];
    modulesList: Module[] = [];
    private _hadUnassignedChip = false;

    dataSource: AllModuleDataSource;
    displayedColumns = ['avatar', 'name', 'version', 'type', 'startDate', 'buttons'];
    isCourseCatalogFeatureEnabled: boolean;

    constructor(
        public dialogRef: MatDialogRef<FilterByModuleComponent>,
        public tagMultiSelectService: TagMultiSelectService,
        public tagService: TagService,
        private _store: Store,
        private _action$: Actions,
        private _translateService: TranslateService,
        private _allUsersService: AllUsersApiService,
        private _featureService: FeatureService
    ) {
        super();

        this.isCourseCatalogFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.CourseCatalog);
    }

    ngOnInit(): void {
        this.loadModulesPage();

        this.tagService.fetchTags();

        this.isUnassignedSelected = this.preSelectUnassigned;
        this.isTableActive = !this.isUnassignedSelected;

        combineLatest([this._allUsersService.filtersList$, this.entitie$])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([filters, modules]) => {
                this.list = filters;
                this._syncUnassignedSelectionFromList();

                const moduleFilterIds = filters.filter((f) => f.entity.type === 'Module').map((filter) => filter.entity.id);

                this.modulesList = Array.isArray(modules) && modules.length ? modules.filter((module) => moduleFilterIds.includes(module.id)) : [];
                this.updateDataSource();
            });

        if (this.isUnassignedSelected && !this._hasUnassignedModuleFilter()) {
            this._applyUnassignedModuleFilter(true);
        }

        this._action$.pipe(ofActionDispatched(FetchAllModulesSuccess), take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.isLoading = false;
        });
    }

    updateDataSource(): void {
        this.dataSource = new AllModuleDataSource(
            this.entitie$.pipe(
                map((modules) => {
                    return (modules || [])
                        .filter((module) => module.status !== 'planned')
                        .map((module) => {
                            module = Object.assign(new Module(), module, {
                                selected: this.list.some((item) => item.entity.id === module.id && item.entity.type === 'Module'),
                            });
                            return module;
                        });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(module: Module): void {
        if (this.isUnassignedSelected || this._hasUnassignedModuleFilter()) {
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.list = this.list.filter((item) => item.entity.type !== UNASSIGNED_MODULE_FILTER_TYPE);
        }

        this.addedToList.emit([
            ...this.list.filter((item) => item.entity.type !== UNASSIGNED_MODULE_FILTER_TYPE),
            { entity: { id: module.id, type: 'Module' }, valueToShow: `${this._translateService.instant('MODULE.SINGULAR')}: ${module.name}` },
        ]);
    }

    removeFromList(module: Module): void {
        this.addedToList.emit(this.list.filter((item) => !(item.entity.id === module.id && item.entity.type === 'Module')));
    }

    onSelection(): void {
        this.isTableActive = !this.isUnassignedSelected;

        if (this.isUnassignedSelected) {
            this._applyUnassignedModuleFilter(true);
            this.choosenModules.emit({ modules: [], isUnassignedSelected: true });
            return;
        }

        this._applyUnassignedModuleFilter(false);
        this.choosenModules.emit({
            modules: this.list.filter((entity) => entity.entity.type === 'Module').map((ent) => ({ id: ent.entity.id } as Module)),
            isUnassignedSelected: false,
        });
    }

    private _hasUnassignedModuleFilter(): boolean {
        return this.list.some((item) => item.entity.type === UNASSIGNED_MODULE_FILTER_TYPE);
    }

    private _syncUnassignedSelectionFromList(): void {
        const hasUnassignedChip = this._hasUnassignedModuleFilter();

        if (hasUnassignedChip) {
            this._hadUnassignedChip = true;
            this.isTableActive = false;

            if (!this.isUnassignedSelected) {
                this.isUnassignedSelected = true;
                this.choosenModules.emit({ modules: [], isUnassignedSelected: true });
            }
            return;
        }

        if (this._hadUnassignedChip && this.isUnassignedSelected) {
            this._hadUnassignedChip = false;
            this.isUnassignedSelected = false;
            this.isTableActive = true;
            this.choosenModules.emit({
                modules: this.list.filter((entity) => entity.entity.type === 'Module').map((ent) => ({ id: ent.entity.id } as Module)),
                isUnassignedSelected: false,
            });
            return;
        }

        this._hadUnassignedChip = false;
        this.isTableActive = !this.isUnassignedSelected;
    }

    private _applyUnassignedModuleFilter(isSelected: boolean): void {
        if (isSelected) {
            const nonModuleFilters = this.list.filter((item) => item.entity.type !== 'Module' && item.entity.type !== UNASSIGNED_MODULE_FILTER_TYPE);

            const updatedList: IChoosenFilterInterface[] = [
                ...nonModuleFilters,
                {
                    entity: { id: UNASSIGNED_MODULE_FILTER_ID, type: UNASSIGNED_MODULE_FILTER_TYPE },
                    valueToShow: `${this._translateService.instant('MODULE.SINGULAR')}: ${this._translateService.instant('GENERAL.LABEL.UNASSIGNED')}`,
                },
            ];

            this.list = updatedList;
            this.updateDataSource();
            this.addedToList.emit(updatedList);
            return;
        }

        if (!this._hasUnassignedModuleFilter()) {
            return;
        }

        const updatedList = this.list.filter((item) => item.entity.type !== UNASSIGNED_MODULE_FILTER_TYPE);
        this.list = updatedList;
        this.addedToList.emit(updatedList);
    }

    loadModulesPage(event?: IAllModuleDialogListInterface): void {
        this._store.dispatch(
            new FetchAllModules({
                filterValues: null,
                search: event?.search || '',
                sortBy: event?.sort?.active || '',
                sortOrder: event?.sort?.direction || '',
                pageSize: event?.paginator?.pageSize || 5,
                pageNumber: event?.paginator?.pageIndex + 1 || 1,
                patchState: false,
                context: event?.context || this.getModulesContext(),
            })
        );
    }

    private getModulesContext(): string {
        return this.isCourseCatalogFeatureEnabled ? FilterByModuleComponent.BOOKING_CONTEXT : '';
    }
}
